import {
  DEFAULT_LOCALE,
  EN_US_SHELL,
  type Locale,
  type MessageCatalog,
} from '@hibro/shared';
import { IntlMessageFormat } from 'intl-messageformat';
import { createContext, useContext } from 'react';

/**
 * The translation surface a component consumes (见 14 / §5.11). `t`
 * formats an ICU message by key; `title` resolves a contributed `%key%`
 * placeholder scoped to its module. Created from a `(locale, catalog)` snapshot
 * with per-key compiled-`IntlMessageFormat` caching.
 */
export interface Translator {
  readonly locale: Locale;
  /** Format the ICU message `key` with `args`; missing key -> the key itself. */
  readonly t: (key: string, args?: Record<string, unknown>) => string;
  /**
   * Resolve a contributed title (架构方案 §5.3): a `%key%` placeholder maps to
   * `t('<moduleId>:<key>')`; a plain string is returned unchanged. A missing
   * translation degrades to the inner key name.
   */
  readonly title: (moduleId: string, raw: string) => string;
}

const PLACEHOLDER = /^%(.+)%$/;

/** Build a {@link Translator} over a catalog snapshot (pure; unit-testable). */
export function createTranslator(
  locale: Locale,
  catalog: MessageCatalog,
): Translator {
  const cache = new Map<string, IntlMessageFormat>();

  const t = (key: string, args?: Record<string, unknown>): string => {
    const message = catalog[key];
    if (message === undefined) {
      return key;
    }
    let compiled = cache.get(key);
    if (!compiled) {
      try {
        compiled = new IntlMessageFormat(message, locale);
      } catch {
        return message;
      }
      cache.set(key, compiled);
    }
    try {
      return String(compiled.format(args));
    } catch {
      return message;
    }
  };

  const title = (moduleId: string, raw: string): string => {
    const match = PLACEHOLDER.exec(raw);
    if (!match) {
      return raw;
    }
    const inner = match[1];
    const key = `${moduleId}:${inner}`;
    return catalog[key] !== undefined ? t(key) : inner;
  };

  return { locale, t, title };
}

// Seed with the built-in English shell so the very first paint (before the async
// catalog arrives) shows real copy instead of raw keys. The `I18nProvider`
// (i18n-provider.tsx) supplies the live translator.
export const TranslatorContext = createContext<Translator>(
  createTranslator(DEFAULT_LOCALE, EN_US_SHELL),
);

/** Access the full {@link Translator} (locale + `t` + `title`). */
export function useTranslator(): Translator {
  return useContext(TranslatorContext);
}

/** Access just the `t` formatter (the common case). */
export function useT(): Translator['t'] {
  return useContext(TranslatorContext).t;
}
